<section class="bg-star-gray-600 pt-[40px] pb-[60px] lg:pt-[64px] lg:pb-[80px]">
  <div class="mx-auto flex max-w-[1000px] flex-col gap-[40px] px-[20px] lg:flex-row lg:items-center lg:gap-[48px]">
    <div class="flex-1 lg:flex-[1.45]">
      <h1 class="text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[44px]">
        Chainalysis Alternative for Crypto AML Compliance
      </h1>
      <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">Teams looking for a Chainalysis alternative are rarely arguing with blockchain analytics as an idea. They want wallet screening and transaction monitoring a small compliance team can run day to day, a way to start that does not begin with a procurement cycle, and a risk picture that does not rest on a single dataset. This page sets out what each platform states publicly about its own products, so the comparison is yours to make.</p>
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow mt-[28px] min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
      <p class="mt-[16px] text-[14px] leading-[1.6] text-white/50">Screening and monitoring run inside the AMLOfficer application, so a business account is the first step. Terms depend on your volumes and are shown in your account after registration.</p>
    </div>
    <div class="flex flex-1 justify-center">
      <img
        src="./images/aml-providers.webp"
        alt="AML risk reports from several data providers opened side by side"
        width="337"
        height="213"
        class="w-full max-w-[337px] object-contain"
      />
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Who ends up looking for an alternative</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Chainalysis is the reference point in this market, which is exactly why the search happens:
      people compare against it rather than start from it. Four situations come up repeatedly, and
      none of them is a complaint about the analytics.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">You are not the buyer the product line is built around.</span> The Chainalysis site presents its products for centralised exchanges, financial institutions, government and law-enforcement agencies, regulators and web3 security teams. A regional VASP, an OTC desk or a payment service can buy into that world, but it is not the shape of the business the pages are written for.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">You want to see terms before you speak to anyone.</span> Chainalysis does not publish prices; each product page ends in a demo request. That is a normal enterprise motion, and it is also the reason "chainalysis pricing" is searched more often than "chainalysis alternative".</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">You want a second reading of the same address.</span> A vendor built on its own blockchain data platform gives you one methodology, applied consistently. Some compliance teams want consistency; others want to see where independent datasets disagree.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Your compliance function is a team, not a department.</span> Two or three people who also do onboarding and support have different needs from a dedicated investigations unit: fewer seats, a shorter path from alert to decision, notifications where the team already talks.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">What Chainalysis publishes about its products</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Read on 8 September 2026, from the vendor's own product pages. Nothing below is our assessment
      of quality; it is what the pages state.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">KYT</span> is described as assessing "risk of incoming and outgoing cryptocurrency transactions on your platform", monitoring "in real time" and generating "alerts within seconds of a transaction occurring on-chain", with a KYT API for integration and case management for handling alerts in bulk. The same page states support for "400+ networks and 50M+ tokens".</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Address Screening</span> is offered through "an easy-to-use UI and integrated via high-reliability, low-latency APIs for real-time screening", returning risk scores with direct and indirect exposure, and continuing to monitor an address after the first check.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Reactor</span> is the investigation product — "investigate and trace funds across blockchains" — alongside Sentinel for stablecoin risk, VASP Risking, Alterya for fraud prevention and Hexagate for web3 security. It is a broad product line, and most of it sits outside the AML screening question.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Sanctions screening is free.</span> Chainalysis publishes an on-chain oracle and a public sanctions screening API that, in its own words, do "not require a customer relationship with Chainalysis", covering designations from lists such as those of the US, EU and UN. If sanctions screening alone is your requirement, that is worth knowing before you compare anything else.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span><span class="text-white">Pricing is not published anywhere on the site.</span> The call to action on the product pages is "Request a demo".</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">How AMLOfficer approaches the same work</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer is an aggregator. It checks wallets and transactions through four AML data
      providers, including BitOK, Global Ledger and Beosin, from one account and one integration.
      The reason for that design is simple and it is the whole argument for the model: providers
      draw on different datasets, and an address one of them calls clean is regularly flagged by
      another.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Around that sit the parts a working compliance team touches every day. Wallets can be checked
      manually from the dashboard or tracked on an ongoing basis, with real-time alerts when
      something changes. Colleagues are invited into the same account with role management, and
      notifications can land in a Telegram group rather than in one person's inbox. Two API
      surfaces are offered: Fast API, which works alongside the dashboard and its reports, and Pro
      API for integrating AML data directly into your own systems in real time.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      One further difference is not a screening feature at all. AMLOfficer's team works on
      recovering stolen assets, so an alert can turn into an action rather than an entry in a log.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Comparison</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Only dimensions where both sides publish something are compared. Where a vendor does not state
      a fact publicly, the cell says so rather than guessing.
    </p>
<div class="mt-[28px] overflow-hidden rounded-[10px] border border-solid border-white/20">
  <div class="hidden bg-[#25282D] lg:grid lg:grid-cols-[1.05fr_1fr_1fr]">
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white/60">What you are comparing</div>
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white">AMLOfficer</div>
    <div class="px-[20px] py-[16px] text-[15px] font-medium text-white">Chainalysis</div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Platform approach</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Aggregator: one platform in front of several AML data providers.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Single-vendor platform built on the company's own blockchain data, presented as "The Blockchain Data Platform".
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Data-provider model</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Four providers, including BitOK, Global Ledger and Beosin, queried for the same address.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Chainalysis data and methodology; the site does not present the product as an aggregator of third-party AML datasets.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Wallet / address screening</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Yes — manual checks and tracked wallets from the dashboard or the API.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Yes — Address Screening, offered through a UI and screening APIs.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Ongoing transaction monitoring</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Yes, with real-time alerts.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Yes — KYT, with alerts "within seconds of a transaction occurring on-chain".
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">API</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Fast API for dashboard-side work and Pro API for integrating AML data into your own systems.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Yes — KYT API and screening APIs; access follows a demo and a commercial agreement.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Team workflow</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Team invitations with role management; alerts can be sent to a Telegram group.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Case management: alerts handled in bulk and worked collaboratively by a compliance team.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Investigations tooling</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      No separate investigation product is published; the team assists with recovering stolen assets.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Yes — Reactor for tracing funds across blockchains, plus Sentinel, VASP Risking, Alterya and Hexagate.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Published asset coverage</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Not published; coverage follows the underlying providers. Check with provider.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      "400+ networks and 50M+ tokens", stated on the KYT page.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Free entry point</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      Not published. Checks run inside a registered account.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      Free sanctions screening oracle and public API, stated to require no customer relationship.
    </div>
  </div>
  <div class="flex flex-col gap-[12px] p-[20px] border-t border-solid border-white/10 lg:grid lg:grid-cols-[1.05fr_1fr_1fr] lg:gap-0 lg:p-0">
    <div class="text-[16px] font-medium leading-[1.5] text-white lg:px-[20px] lg:py-[18px]">Published pricing</div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">AMLOfficer</span>
      No public price list. Terms depend on volumes and are shown in your account after registration.
    </div>
    <div class="text-[15px] leading-[1.7] text-white/80 lg:px-[20px] lg:py-[18px]">
      <span class="mb-[4px] block text-[12px] uppercase tracking-[0.6px] text-white/40 lg:hidden">Chainalysis</span>
      No public price list. The product pages route to "Request a demo".
    </div>
  </div>
</div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">When Chainalysis is still the right answer</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      A comparison page that never reaches this section is an advertisement. There are requirements
      where the aggregator model is not the better fit, and they are easy to recognise.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You investigate rather than screen. Tracing funds across chains, building a case file and working alongside law enforcement is what Reactor is for, and AMLOfficer publishes no equivalent product.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Your requirement names the vendor. Some banking partners, auditors and licence conditions ask for a specific analytics provider; in that case the evaluation is already decided.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Your exposure is in web3 security or stablecoin issuance rather than counterparty screening — Hexagate and Sentinel address problems that a wallet check does not.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You need the published breadth of chain and token coverage as a contractual fact, stated by the vendor you are buying from.</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>You want one methodology applied uniformly across a large organisation, and treat several independent opinions on the same address as noise rather than signal.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">What to compare before you choose</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      The dimensions that decide this choice are rarely the ones in a feature grid. Seven questions
      separate the options quickly.
    </p>
<ul class="mt-[24px] flex flex-col gap-[16px]">
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Whose data is behind the verdict, and are the sources named?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>One reading of an address, or several — and if several, what do you see when they disagree?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>How is risk expressed: a level, a score, an exposure breakdown? Can your policy be written against it as it stands?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>How does the work reach you — a dashboard, an API, a message in the channel your team already uses?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>Who besides the compliance lead can act, and what does adding a colleague cost in seats and in setup?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>What does the first month look like: a demo and a contract, or a registration and a first check?</span>
  </li>
  <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
    <mat-icon
      svgIcon="check-circle-white-marker"
      class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
    ></mat-icon>
    <span>What happens after an alert. Screening ends at the verdict; recovering funds is a different service with a different scope.</span>
  </li>
</ul>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Evaluating a move without disrupting compliance</h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Nobody should switch AML tooling on the strength of a comparison table, this one included. The
      cheap test is to run the two side by side for a short period on addresses you have already
      judged, including the ones you rejected. You know the answers, so you are measuring the tool
      rather than the addresses.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      Three things are worth writing down while you do it: where the verdicts differ and which side
      you would have defended to a regulator, how much work it takes to turn a response into a
      decision in your own systems, and what leaves the platform with you — reports, histories,
      audit trail. Keep your existing coverage running until that evidence exists. An AML control
      that lapses during an evaluation is a worse outcome than either vendor.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">Frequently asked questions</h2>
<div class="mt-[24px] flex flex-col gap-[24px]">
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Is AMLOfficer affiliated with Chainalysis?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">No. AMLOfficer is not affiliated with, endorsed by or a reseller of Chainalysis. This page compares two independent platforms using each vendor's own public statements.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Does AMLOfficer use several AML data providers?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">Yes. AMLOfficer aggregates four AML data providers, including BitOK, Global Ledger and Beosin, and returns their readings of the same wallet or transaction in one place.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Can I use AMLOfficer through an API?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">Yes. Fast API works alongside the dashboard and its reports, while Pro API is intended for integrating AML data directly into your own systems in real time.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">Is pricing public on either side?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">Neither platform publishes a price list. Chainalysis routes buyers to a demo request; AMLOfficer's terms depend on your volumes and appear in your account after registration.</p>
  </div>
  <div>
    <h3 class="text-[19px] font-medium leading-[1.4] text-white lg:text-[21px]">What should I compare first?</h3>
    <p class="mt-[12px] text-[16px] leading-[1.8] text-white/80">The data model and the workflow, in that order: whose datasets stand behind the verdict, and how a verdict reaches the person who has to act on it.</p>
  </div>
</div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <div class="rounded-[10px] border border-solid border-white/20 bg-[#25282D] px-[20px] py-[28px] lg:px-[36px] lg:py-[36px]">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">See the aggregated result on your own addresses</h2>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">The fastest way to judge a screening platform is to point it at wallets whose history you already know. Registering a business account opens the dashboard, the tracked-wallet list and the API surfaces; terms depend on your volumes and are shown in the account.</p>
      <div class="mt-[28px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <a
          [routerLink]="'/contact-us'"
          class="text-[16px] leading-[1.6] text-white underline underline-offset-4 hover:text-white/70"
          >Contact Us</a
        >
      </div>
      <p class="mt-[24px] text-[15px] leading-[1.7] text-white/60">
        Comparing more than one vendor at a time? The free-checker end of the same market is covered on the <a [routerLink]="'/alternatives/amlbot'" class="text-white underline underline-offset-4 hover:text-white/70">AMLBot alternative page</a>.
      </p>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[70px] lg:pb-[90px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[20px] font-medium leading-[1.4] text-white lg:text-[22px]">Sources and date checked</h2>
    <p class="mt-[16px] text-[15px] leading-[1.7] text-white/60">
      Every statement about Chainalysis on this page is taken from Chainalysis's own product pages,
      read on 8 September 2026. Product lines move; open the source before you rely on a line here.
    </p>
    <ul class="mt-[16px] flex flex-col gap-[10px] text-[15px] leading-[1.7] text-white/60">
        <li>
          <a href="https://www.chainalysis.com/product/kyt/" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">Chainalysis - KYT product page</a>
        </li>
        <li>
          <a href="https://www.chainalysis.com/product/address-screening/" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">Chainalysis - Address Screening product page</a>
        </li>
        <li>
          <a href="https://www.chainalysis.com/" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">Chainalysis - product overview</a>
        </li>
        <li>
          <a href="https://go.chainalysis.com/chainalysis-oracle-docs.html" target="_blank" rel="nofollow noopener" class="underline underline-offset-4 hover:text-white">Chainalysis - sanctions screening oracle documentation</a>
        </li>
    </ul>
    <p class="mt-[24px] text-[14px] leading-[1.7] text-white/40">Chainalysis is a trademark of its respective owner. AMLOfficer is not affiliated with or endorsed by Chainalysis, and the name is used here only to identify the platform being compared.</p>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
